import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Autocomplete from "react-google-autocomplete";
import { signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from './Auth';
import './SignUp.css';
import { createUser } from './api';

const SignUp = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        birthDate: '',
        birthTime: '',
        birthLocation: '',
        birthLat: '',
        birthLng: '',
        cosmicQuestion: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePlaceChange = (place) => {
        if (place.geometry?.location) {
            setFormData(prev => ({
                ...prev,
                birthLocation: place.formatted_address,
                birthLat: place.geometry.location.lat(),
                birthLng: place.geometry.location.lng()
            }));
        }
    }

    const validate = () => {
        const newErrors = {};
        
        // Validate required fields
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        
        if (!formData.password) newErrors.password = 'Password is required';
        else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        if (!formData.birthDate) newErrors.birthDate = 'Birth date is required';
        if (!formData.birthTime) newErrors.birthTime = 'Birth time is required';
        if (!formData.birthLocation.trim()) newErrors.birthLocation = 'Birth location is required';
        if (!formData.birthLat) newErrors.birthLocation = 'Birth location is required';
        if (!formData.birthLng) newErrors.birthLocation = 'Birth location is required';
        
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        
        setIsSubmitting(true);

        try {
            await createUser(formData)
            const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password)
            await sendEmailVerification(userCredential.user);

            navigate('/being');
        } catch (error) {
            console.error('Error during sign up:', error);
            setErrors({ 
                submit: error.message || 'Failed to create account. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const goToSignIn = () => {
        navigate('/signin');
    };

    return (
        <div className="signup-container">
            <div className="signup-header">
                <h1 className="signup-title">Join Orb</h1>
                <p className="signup-tagline">Begin your journey</p>
            </div>
            
            <div className="signup-content">
                <form className="signup-form" onSubmit={handleSubmit}>
                    <div className="form-section-signup">
                        <h2 className="section-title">Account Information</h2>
                        
                        <div className="form-group-signup">
                            <label htmlFor="name">Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className="form-input-signup"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your name"
                            />
                            {errors.name && <p className="error-message">{errors.name}</p>}
                        </div>
                        
                        <div className="form-group-signup">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-input-signup"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="your.email@example.com"
                            />
                            {errors.email && <p className="error-message">{errors.email}</p>}
                        </div>
                        
                        <div className="two-column">
                            <div className="column">
                                <div className="form-group-signup">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        className="form-input-signup"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Create a password"
                                    />
                                    {errors.password && <p className="error-message">{errors.password}</p>}
                                </div>
                            </div>
                            
                            <div className="column">
                                <div className="form-group-signup">
                                    <label htmlFor="confirmPassword">Confirm Password</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        className="form-input-signup"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        placeholder="Confirm your password"
                                    />
                                    {errors.confirmPassword && <p className="error-message">{errors.confirmPassword}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="divider">
                        <div className="divider-line"></div>
                    </div>
                    
                    <div className="form-section-signup">
                        <h2 className="section-title">Personal Information</h2>
                        <p className="section-subtitle">For your personalized experience</p>
                        
                        <div className="two-column">
                            <div className="column">
                                <div className="form-group-signup">
                                    <label htmlFor="birthDate">Date of Birth</label>
                                    <input
                                        type="date"
                                        id="birthDate"
                                        name="birthDate"
                                        className="form-input-signup"
                                        value={formData.birthDate}
                                        onChange={handleChange}
                                    />
                                    {errors.birthDate && <p className="error-message">{errors.birthDate}</p>}
                                </div>
                            </div>
                            
                            <div className="column">
                                <div className="form-group-signup">
                                    <label htmlFor="birthTime">Time of Birth</label>
                                    <input
                                        type="time"
                                        id="birthTime"
                                        name="birthTime"
                                        className="form-input-signup"
                                        value={formData.birthTime}
                                        onChange={handleChange}
                                    />
                                    {errors.birthTime && <p className="error-message">{errors.birthTime}</p>}
                                    <p className="input-help">As precise as possible</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="form-group-signup">
                            <label htmlFor="birthLocation">Location of Birth</label>
                            <Autocomplete
                                className="form-input-signup"
                                apiKey="AIzaSyDYulGOMyf17pKKfVdKT6AGy3JolG6B4f0"
                                id="birthLocation"
                                name="birthLocation"
                                value={formData.birthLocation}
                                onChange={handleChange}
                                onPlaceSelected={handlePlaceChange}
                                placeholder="City, State"
                                options={{
                                    fields: ["formatted_address", "geometry", "address_components"]
                                }}
                            />
                            {errors.birthLocation && <p className="error-message">{errors.birthLocation}</p>}
                        </div>
                    </div>
                    
                    <div className="divider">
                        <div className="divider-line"></div>
                    </div>
                    
                    {errors.submit && <p className="error-message submit-error">{errors.submit}</p>}
                    
                    <button type="submit" className="form-button" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating account...' : 'Create Account'}
                    </button>
                </form>
                
                <div className="signin-link">
                    <p>
                        Already have an account? <button onClick={goToSignIn} className="text-button">Sign In</button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignUp;